import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Play, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function AnimatedSection({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className={className}>
      {children}
    </motion.div>
  );
}

const categories = [
  { key: "all", label: "الكل" },
  { key: "marketing", label: "تسويق" },
  { key: "production", label: "إنتاج" },
  { key: "training", label: "تدريب" },
  { key: "ads", label: "إعلانات" },
];

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/portfolio`).then(r => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "all" ? items : items.filter(i => i.category === filter);
  const catLabel = (c) => c === "marketing" ? "تسويق" : c === "production" ? "إنتاج" : c === "ads" ? "إعلانات" : "تدريب";

  return (
    <div data-testid="portfolio-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#111112]">
          <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-primary/8 rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">معرض الأعمال</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              أعمالنا <span className="text-primary">تتحدث</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl">
              مجموعة مختارة من مشاريعنا التي نفخر بها في مجالات التسويق والإنتاج والتدريب.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filters">
            {categories.map(c => (
              <Button
                key={c.key}
                variant={filter === c.key ? "default" : "outline"}
                className={filter === c.key
                  ? "bg-primary hover:bg-[#E54D2E] text-white"
                  : "border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"
                }
                onClick={() => setFilter(c.key)}
                data-testid={`filter-${c.key}`}
              >
                {c.label}
              </Button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item, i) => {
              const ytId = getYouTubeId(item.video_url);
              return (
              <AnimatedSection key={item.id}>
                <div className="group relative rounded-xl overflow-hidden bg-[#161618] border border-white/5 hover:border-primary/20 transition-all" data-testid={`portfolio-card-${i}`}>
                  <div className="aspect-video overflow-hidden relative">
                    {playingVideo === item.id && ytId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
                        className="w-full h-full"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title={item.title}
                      />
                    ) : (
                      <>
                        <img
                          src={ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        {ytId && (
                          <button
                            onClick={() => setPlayingVideo(item.id)}
                            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors cursor-pointer"
                            data-testid={`play-video-${i}`}
                          >
                            <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center shadow-lg shadow-primary/30 hover:scale-110 transition-transform">
                              <Play size={24} className="text-white ms-1" fill="white" />
                            </div>
                          </button>
                        )}
                      </>
                    )}
                    {playingVideo === item.id && (
                      <button
                        onClick={() => setPlayingVideo(null)}
                        className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="p-5">
                    <span className="text-primary text-xs font-medium">{catLabel(item.category)}</span>
                    <h3 className="text-white font-semibold font-['Zain'] mt-1 mb-1">{item.title}</h3>
                    <p className="text-zinc-400 text-sm line-clamp-2">{item.description}</p>
                    {item.client_name && (
                      <p className="text-zinc-500 text-xs mt-2">العميل: {item.client_name}</p>
                    )}
                  </div>
                </div>
              </AnimatedSection>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-zinc-500">
              لا توجد أعمال في هذا القسم حاليًا
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
